import {
  Alert,
  Button,
  FieldGroup,
  FormField,
  Input,
  Modal,
  MultiSelect,
  RepeatableFieldGroup,
  Select,
  type MultiSelectOption,
  type SelectOption,
} from '@hkdevs/design-system';
import { useState } from 'react';
import { ApiError } from '../../api/client';
import type { AdminUser, Role, UserCreate } from '../../api/queries/users';
import { useEnabledRoles } from '../../api/queries/roles';
import { offersRole } from '../../auth/roles';

/**
 * Create a user, or edit one (pc-dbb.7): name and email (required), roles, supervisors,
 * typed external IDs, and when editing the status. The API's field errors show on the
 * fields they name.
 */

export interface ExternalIdRow {
  typeId: string;
  value: string;
}

export interface UserFormValues {
  name: string;
  email: string;
  roles: Role[];
  /** Supervisors' user ids. */
  supervisorIds: string[];
  externalIds: ExternalIdRow[];
  /** Edit only. */
  enabled: boolean;
}

export const EMPTY_USER: UserFormValues = {
  name: '',
  email: '',
  roles: [],
  supervisorIds: [],
  externalIds: [],
  enabled: true,
};

/**
 * The form's values for an existing user. Operator isn't one of them: it's granted on the
 * host, and the API keeps it while the user keeps Admin (ADR 0016's A25 amendment).
 */
export function userValues(user: AdminUser): UserFormValues {
  return {
    name: user.name,
    email: user.email,
    roles: user.roles.filter((role) => role !== 'operator'),
    supervisorIds: user.supervisors.map((supervisor) => supervisor.id),
    externalIds: user.externalIds.map((id) => ({ typeId: id.typeId, value: id.value })),
    enabled: user.enabled,
  };
}

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  operator: 'Operator',
  supervisor: 'Supervisor',
  rep: 'Rep',
  rep_intern: 'Rep (intern)',
  pay_viewer: 'Pay viewer',
};

const ROLE_OPTIONS: MultiSelectOption[] = (
  ['admin', 'pay_viewer', 'supervisor', 'rep', 'rep_intern'] as const
).map((role) => ({ value: role, label: ROLE_LABELS[role] }));

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
];

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Errors by field; an external ID's by its row in the form. */
export interface UserFormErrors {
  name?: string;
  email?: string;
  roles?: string;
  supervisorIds?: string;
  externalIds?: string;
  externalIdRows?: Record<number, { typeId?: string; value?: string }>;
  /** Not about one field: the API's detail. */
  form?: string;
}

/** What's wrong with the values, in words that explain the fix. */
export function validateUser(values: UserFormValues): UserFormErrors {
  const errors: UserFormErrors = {};
  if (!values.name.trim()) errors.name = 'Enter the user’s name.';
  if (!values.email.trim()) errors.email = 'Enter the user’s email address.';
  else if (!EMAIL.test(values.email.trim()))
    errors.email = 'Enter an email address like name@example.com.';
  return errors;
}

/** The rows sent: those with a value. Their indexes in the form, to map errors back. */
function keptRows(values: UserFormValues) {
  return values.externalIds.flatMap((row, index) =>
    row.value.trim() ? [{ index, typeId: row.typeId, value: row.value.trim() }] : [],
  );
}

/** The request body: trimmed, and without empty external IDs. No role makes a rep. */
export function toUserBody(values: UserFormValues): UserCreate {
  return {
    name: values.name.trim(),
    email: values.email.trim(),
    roles: values.roles,
    supervisorIds: values.supervisorIds,
    externalIds: keptRows(values).map(({ typeId, value }) => ({ typeId, value })),
  };
}

/**
 * The API's error as form errors: each field error under its field (the location may
 * start with `body`), anything else as the form's error. Undefined for errors that aren't
 * about the input (a network failure, a 5xx), which the caller shows as a toast.
 */
export function formErrors(error: unknown, values: UserFormValues): UserFormErrors | undefined {
  if (!(error instanceof ApiError) || error.status >= 500) return undefined;
  const rows = keptRows(values);
  const errors: UserFormErrors = {};
  for (const fieldError of error.problem.errors ?? []) {
    const loc = fieldError.loc[0] === 'body' ? fieldError.loc.slice(1) : fieldError.loc;
    const [field, index, part] = loc;
    if (field === 'name' || field === 'email' || field === 'roles') {
      errors[field] ??= fieldError.message;
    } else if (field === 'supervisorIds') {
      errors.supervisorIds ??= fieldError.message;
    } else if (field === 'externalIds' && typeof index === 'number' && rows[index]) {
      const row = rows[index].index;
      const key = part === 'typeId' ? 'typeId' : 'value';
      errors.externalIdRows = {
        ...errors.externalIdRows,
        [row]: { ...errors.externalIdRows?.[row], [key]: fieldError.message },
      };
    } else if (field === 'externalIds') {
      errors.externalIds ??= fieldError.message;
    } else {
      errors.form ??= fieldError.message;
    }
  }
  if (Object.keys(errors).length === 0) {
    errors.form = error.problem.detail ?? 'The user couldn’t be saved.';
  }
  return errors;
}

export interface UserModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  /** The form's values when it opens: empty to create, the user's to edit. */
  initialValues?: UserFormValues;
  /** Everyone who can supervise: value is the user id, label the name. */
  supervisorOptions: MultiSelectOption[];
  /** The external ID types. */
  externalIdTypes: SelectOption[];
  /** Called with valid values. */
  onSubmit: (values: UserFormValues) => void;
  /** The save is in flight: the fields and buttons wait. */
  submitting?: boolean;
  /** The API's errors for the last save. */
  serverErrors?: UserFormErrors | undefined;
  /** The user holds operator: shown read-only, since only the host grants it. */
  operator?: boolean;
}

/** Create a user, or edit one. Remount it (a `key`) to start again from initialValues. */
export function UserModal({
  open,
  onClose,
  mode,
  initialValues = EMPTY_USER,
  supervisorOptions,
  externalIdTypes,
  onSubmit,
  submitting = false,
  serverErrors,
  operator = false,
}: UserModalProps) {
  const [values, setValues] = useState(initialValues);
  const [showErrors, setShowErrors] = useState(false);
  const local = showErrors ? validateUser(values) : {};
  const errors: UserFormErrors = { ...serverErrors, ...local };
  const set = <K extends keyof UserFormValues>(key: K, value: UserFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));
  const editing = mode === 'edit';
  const firstType = externalIdTypes[0]?.value ?? '';
  const { data: enabledRoles } = useEnabledRoles();
  // Switched-off roles aren't offered, unless the user already holds one (ADR 0028).
  const roleOptions = ROLE_OPTIONS.filter((option) =>
    offersRole(enabledRoles, option.value as Role, initialValues.roles),
  );

  const submit = () => {
    setShowErrors(true);
    if (Object.keys(validateUser(values)).length) return;
    onSubmit(values);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={editing ? 'Edit user' : 'Create user'}
      dismissable={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            loading={submitting}
            loadingText={editing ? 'Saving…' : 'Creating…'}
          >
            {editing ? 'Save' : 'Create user'}
          </Button>
        </>
      }
    >
      <FieldGroup>
        {errors.form && (
          <Alert tone="danger" title="The user couldn’t be saved">
            {errors.form}
          </Alert>
        )}
        <FormField label="Name" required error={errors.name}>
          <Input
            value={values.name}
            autoComplete="off"
            disabled={submitting}
            onChange={(event) => set('name', event.target.value)}
          />
        </FormField>
        <FormField label="Email" required error={errors.email}>
          <Input
            type="email"
            value={values.email}
            autoComplete="off"
            disabled={submitting}
            onChange={(event) => set('email', event.target.value)}
          />
        </FormField>
        <FormField
          label="Roles"
          hint={
            operator
              ? 'Leave empty for Rep. Also Operator, which is granted on the host: removing Admin or disabling the user removes it.'
              : 'Leave empty for Rep.'
          }
          error={errors.roles}
        >
          <MultiSelect
            options={roleOptions}
            value={values.roles}
            placeholder="Select roles"
            disabled={submitting}
            onChange={(roles) => set('roles', roles as Role[])}
          />
        </FormField>
        <FormField
          label="Supervisors"
          hint="Leave empty for no supervisor."
          error={errors.supervisorIds}
        >
          <MultiSelect
            options={supervisorOptions}
            value={values.supervisorIds}
            placeholder="Select supervisors"
            disabled={submitting}
            onChange={(supervisorIds) => set('supervisorIds', supervisorIds)}
          />
        </FormField>
        <RepeatableFieldGroup<ExternalIdRow>
          legend="External IDs"
          itemLabel="External ID"
          rows={values.externalIds}
          onChange={(rows) => set('externalIds', rows)}
          createRow={() => ({ typeId: firstType, value: '' })}
          max={externalIdTypes.length}
          maxReason={
            externalIdTypes.length
              ? 'A user has at most one ID of each type.'
              : 'There are no external ID types yet.'
          }
          disabled={submitting}
          error={errors.externalIds}
          renderRow={(row, index, update) => (
            <>
              <FormField
                label={`External ID ${index + 1} type`}
                error={errors.externalIdRows?.[index]?.typeId}
              >
                <Select
                  options={externalIdTypes}
                  value={row.typeId}
                  onChange={(event) => update({ ...row, typeId: event.target.value })}
                />
              </FormField>
              <FormField
                label={`External ID ${index + 1} value`}
                error={errors.externalIdRows?.[index]?.value}
              >
                <Input
                  value={row.value}
                  onChange={(event) => update({ ...row, value: event.target.value })}
                />
              </FormField>
            </>
          )}
        />
        {editing && (
          <FormField label="Status" hint="A disabled user can’t sign in.">
            <Select
              options={STATUS_OPTIONS}
              value={values.enabled ? 'active' : 'disabled'}
              disabled={submitting}
              onChange={(event) => set('enabled', event.target.value === 'active')}
            />
          </FormField>
        )}
      </FieldGroup>
    </Modal>
  );
}
